import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { omit } from 'lodash';
import { useListProductsQuery } from 'renderer/features/products-page/services/products.endpoints';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { dropdownKeyEventHandler } from 'renderer/shared/callbacks/keyEventHandler';
import { Discount } from '../schemas/discounts.schemas';
import {
  amountUnitsOptions,
  copyLimitScopeOptions,
  durationOptions,
  minimumUnitsOptions,
  submitButtonTextOptions,
} from '../constants/discounts.constants';
import {
  useCreateDiscountMutation,
  useUpdateDiscountMutation,
} from '../services/discounts.endpoints';

type DiscountFormValues = Omit<Discount, 'amount'> & {
  amount: number | '';
};

const initialDiscountForm: DiscountFormValues = {
  code: '',
  name: '',
  amount: '',
  amountUnits: 'dollars',
  start: moment().toISOString(),
  end: moment().add(1, 'day').toISOString(),
  duration: 'once',
  copyLimitScope: 'item',
  productIds: [],
  _id: '',
};

const parseNonNegativeNumberInput = (
  value: string,
  valueAsNumber: number,
): number | '' => {
  if (value === '') return '';
  const parsed = Number.isFinite(valueAsNumber) ? valueAsNumber : Number(value);
  if (!Number.isFinite(parsed)) return '';
  return Math.max(0, parsed);
};

const toDiscountAmount = (amount: number | ''): number =>
  amount === '' ? 0 : amount;

// Darker than MUI's default helper text (especially when the field is disabled).
const darkHelperTextProps = {
  sx: {
    color: 'grey.800',
    '&.Mui-disabled': { color: 'grey.800' },
  },
};

interface DiscountFormProps {
  mode: 'edit' | 'create';
  discount?: Discount | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  /** After a successful create, parent should switch this dialog into edit mode. */
  onCreated?: (discount: Discount) => void;
}

// Duration only matters for subscription billing (once vs forever renewals).
// It is irrelevant when the discount cannot apply to any subscription product.
const discountAppliesToSubscriptions = (
  productIds: string[],
  products: { _id: string; category: string }[] | undefined,
): boolean => {
  if (productIds.length === 0) return true; // all products, subs included
  if (!products?.length) return true; // unknown catalog — don't block
  return productIds.some((id) => {
    const product = products.find((p) => p._id === id);
    return product?.category === 'Subscription';
  });
};

const DiscountForm = ({
  mode,
  discount,
  open,
  setOpen,
  onCreated,
}: DiscountFormProps) => {
  const {
    data: products,
    isLoading: productsLoading,
    error: productsError,
  } = useListProductsQuery({ includeHidden: true });
  const [
    createDiscount,
    { isLoading: createLoading, error: createError, reset: resetCreate },
  ] = useCreateDiscountMutation();
  const [
    updateDiscount,
    {
      isLoading: updateLoading,
      error: updateError,
      isSuccess: updateSuccess,
      reset: resetUpdate,
    },
  ] = useUpdateDiscountMutation();

  const [discountForm, setDiscountForm] = useState(initialDiscountForm);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'edit' && discount) {
      setDiscountForm(discount);
    }
  }, [discount, mode]);

  useEffect(() => {
    if (!open) {
      setSuccessMessage(null);
      return;
    }
    if (mode === 'create') {
      setDiscountForm(initialDiscountForm);
      setSuccessMessage(null);
      resetCreate();
      resetUpdate();
    }
    // Reset only when the dialog opens or mode changes — not when reset fn
    // identities change mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, open]);

  useEffect(() => {
    if (updateSuccess) {
      setSuccessMessage('Discount edited!');
    }
  }, [updateSuccess]);

  const isLoading = productsLoading || createLoading || updateLoading;
  const error = mode === 'create' ? createError : updateError ?? productsError;
  const submitButtonText = submitButtonTextOptions[mode];

  const getInputSafeOptionalValue = (value: string | number | undefined) =>
    value === undefined ? '' : value;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDiscountForm({
      ...discountForm,
      [event.target.name]: event.target.value,
    });
  };

  const handleOptionalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'minimumUnits') {
      setDiscountForm({
        ...discountForm,
        minimumUnits:
          value === '' ? undefined : (value as 'copies' | 'dollars'),
        // Clearing units clears the amount so a stale min isn't left on "No Minimum".
        ...(value === '' ? { minimum: undefined } : {}),
      });
      return;
    }
    setDiscountForm({
      ...discountForm,
      [name]: value === '' ? undefined : value,
    });
  };

  const durationRelevant = discountAppliesToSubscriptions(
    discountForm.productIds,
    products,
  );

  const handleSubmit = async () => {
    const payload = {
      ...discountForm,
      amount: toDiscountAmount(discountForm.amount),
    };
    if (mode === 'create') {
      try {
        const created = await createDiscount(omit(payload, '_id')).unwrap();
        setDiscountForm(created);
        setSuccessMessage('Discount created!');
        onCreated?.(created);
      } catch {
        // Mutation error state is rendered below.
      }
      return;
    }
    if (mode === 'edit') {
      updateDiscount(payload);
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogTitle>
        {mode === 'edit' ? 'Edit Discount' : 'Add a New Discount'}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => setOpen(false)}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <DialogContentText>
          {mode === 'edit'
            ? 'Update the details of this discount.'
            : 'Enter the details of the new discount.'}
        </DialogContentText>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              autoFocus
              size="small"
              name="code"
              id="code"
              margin="dense"
              label="Code"
              fullWidth
              required
              disabled={isLoading}
              value={discountForm.code}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              autoFocus
              size="small"
              name="name"
              id="name"
              margin="dense"
              label="Brief Description"
              fullWidth
              required
              // helperText="This will show on the purchase page when a discount is applied"
              disabled={isLoading}
              value={discountForm.name}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              autoFocus
              size="small"
              name="amount"
              id="amount"
              margin="dense"
              label="Amount"
              type="number"
              fullWidth
              required
              inputProps={{ min: 0 }}
              helperText="Cannot be edited after being created"
              FormHelperTextProps={darkHelperTextProps}
              disabled={isLoading || mode === 'edit'}
              value={discountForm.amount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDiscountForm({
                  ...discountForm,
                  amount: parseNonNegativeNumberInput(
                    e.target.value,
                    e.target.valueAsNumber,
                  ),
                })
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              autoFocus
              size="small"
              name="amountUnits"
              id="amountUnits"
              margin="dense"
              label="Units of Amount"
              select
              fullWidth
              required
              helperText="Cannot be edited after being created"
              FormHelperTextProps={darkHelperTextProps}
              disabled={isLoading || mode === 'edit'}
              value={discountForm.amountUnits}
              onChange={handleChange}
            >
              {amountUnitsOptions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <DatePicker
              label="Discount Starts"
              name="starts"
              // disabled={isLoading}
              slotProps={{
                textField: { required: true, size: 'small' },
              }}
              disabled={isLoading}
              value={dayjs(discountForm.start)}
              onChange={(start) => {
                setDiscountForm({
                  ...discountForm,
                  start: start ? start.toISOString() : '',
                });
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <DatePicker
              label="Discount Ends"
              name="ends"
              // disabled={isLoading}
              slotProps={{
                textField: { required: true, size: 'small' },
              }}
              disabled={isLoading}
              value={dayjs(discountForm.end)}
              onChange={(end) => {
                setDiscountForm({
                  ...discountForm,
                  end: end ? end.toISOString() : '',
                });
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              autoFocus
              size="small"
              name="duration"
              id="duration"
              margin="dense"
              label="Apply to Subscription"
              helperText="Subscriptions only. Does not affect plugin purchases. Cannot be edited after being created"
              FormHelperTextProps={darkHelperTextProps}
              // Subscription billing only: once = first period, forever = all renewals.
              // helperText={
              //   durationRelevant
              //     ? 'Subscriptions only: Once discounts the first billing period; Forever discounts every renewal; plugins are unaffected.'
              //     : 'Not applicable — this discount has no subscription products.'
              // }
              select
              fullWidth
              // Stripe coupon duration is immutable after create (same as amount).
              disabled={isLoading || !durationRelevant || mode === 'edit'}
              value={getInputSafeOptionalValue(discountForm.duration)}
              onChange={handleOptionalChange}
              onKeyUp={dropdownKeyEventHandler(durationOptions, (option) =>
                setDiscountForm({
                  ...discountForm,
                  duration: option as 'once' | 'forever',
                }),
              )}
            >
              {durationOptions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              autoFocus
              size="small"
              name="minimumUnits"
              id="minimumUnits"
              margin="dense"
              label="Minimum Units"
              select
              fullWidth
              disabled={isLoading}
              value={getInputSafeOptionalValue(discountForm.minimumUnits)}
              onChange={handleOptionalChange}
            >
              {minimumUnitsOptions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              autoFocus
              size="small"
              name="minimum"
              id="minimum"
              margin="dense"
              label="Minimum Amount"
              type="number"
              fullWidth
              inputProps={{ min: 0 }}
              disabled={isLoading}
              value={getInputSafeOptionalValue(discountForm.minimum)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const next =
                  e.target.value === ''
                    ? undefined
                    : Math.max(0, e.target.valueAsNumber);
                setDiscountForm({
                  ...discountForm,
                  minimum: next,
                  // Typing a min with no units → default to dollars so the field is usable.
                  ...(next !== undefined &&
                  discountForm.minimumUnits === undefined
                    ? { minimumUnits: 'dollars' as const }
                    : {}),
                });
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              autoFocus
              size="small"
              name="quantityAvailable"
              id="quantityAvailable"
              margin="dense"
              label="Quantity Available"
              type="number"
              fullWidth
              helperText="Total uses across all orders. Leave empty for unlimited."
              FormHelperTextProps={darkHelperTextProps}
              inputProps={{ min: 0 }}
              disabled={isLoading}
              value={getInputSafeOptionalValue(discountForm.quantityAvailable)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDiscountForm({
                  ...discountForm,
                  quantityAvailable:
                    e.target.value === ''
                      ? undefined
                      : Math.max(0, e.target.valueAsNumber),
                })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              autoFocus
              size="small"
              name="copyLimitScope"
              id="copyLimitScope"
              margin="dense"
              label="Discount Applies To"
              select
              fullWidth
              helperText={
                <>
                  <strong>Item (default):</strong> full amount off each eligible
                  item.
                  <br />
                  <strong>Order:</strong> applied once to the order total.
                </>
              }
              FormHelperTextProps={darkHelperTextProps}
              disabled={isLoading}
              value={discountForm.copyLimitScope ?? 'item'}
              onChange={handleOptionalChange}
            >
              {copyLimitScopeOptions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {products && (
            <Grid item xs={12}>
              <TextField
                autoFocus
                size="small"
                name="productIds"
                id="productIds"
                margin="dense"
                label="Products"
                select
                SelectProps={{ multiple: true }}
                fullWidth
                helperText="Leaving this field empty will allow this discount on all products"
                FormHelperTextProps={darkHelperTextProps}
                disabled={isLoading}
                value={discountForm.productIds}
                onChange={handleChange}
              >
                {products.map(({ _id, name }) => (
                  <MenuItem key={_id} value={_id}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">
                {mode === 'create'
                  ? 'Error creating discount: '
                  : 'Error editing discount: '}
                {formatQueryError(error)}
              </Alert>
            </Grid>
          )}
          {successMessage && (
            <Grid item xs={12}>
              <Alert severity="success">{successMessage}</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button
          disabled={isLoading || discountForm.amount === ''}
          onClick={handleSubmit}
        >
          {isLoading ? <CircularProgress /> : submitButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiscountForm;
